import { useEffect, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { Button, ScrollArea } from '@/components/ui'
import { ArrowLeft, FileText, Loader2, AlertCircle } from 'lucide-react'
import { memosApi } from '@/services/api'
import { MemoDetail } from '@/types'

const growthMemoStyles = `
  .growth-memo-content {
    font-family: 'Inter', system-ui, sans-serif;
    line-height: 1.75;
    color: #0f172a;
  }

  .growth-memo-content h1 {
    font-size: 1.75rem;
    font-weight: 800;
    color: #0f172a;
    margin: 1.25rem 0 1rem;
    padding-bottom: 0.75rem;
    border-bottom: 2px solid #e2e8f0;
  }

  .growth-memo-content h2 {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1e3a8a;
    margin: 1.5rem 0 0.75rem;
  }

  .growth-memo-content h3 {
    font-size: 1.05rem;
    font-weight: 700;
    color: #334155;
    margin: 1.25rem 0 0.5rem;
  }

  .growth-memo-content p {
    margin: 0.75rem 0;
    color: #334155;
  }

  .growth-memo-content strong {
    color: #0f172a;
    font-weight: 700;
  }

  .growth-memo-content ul, .growth-memo-content ol {
    margin: 0.75rem 0;
    padding-left: 1.25rem;
    color: #334155;
  }

  .growth-memo-content li {
    margin: 0.35rem 0;
  }

  .growth-memo-content hr {
    border: none;
    height: 1px;
    background: #e2e8f0;
    margin: 1.5rem 0;
  }

  .growth-memo-content blockquote {
    border-left: 4px solid #0ea5e9;
    background: #f8fafc;
    padding: 0.75rem 1rem;
    margin: 1rem 0;
    border-radius: 0 8px 8px 0;
    color: #0f172a;
  }

  .growth-memo-content table {
    width: 100%;
    border-collapse: collapse;
    margin: 1rem 0;
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 1px 2px rgba(0,0,0,0.06);
  }

  .growth-memo-content th {
    text-align: left;
    font-size: 0.85rem;
    padding: 0.75rem;
    background: #0f172a;
    color: white;
  }

  .growth-memo-content td {
    padding: 0.75rem;
    border-top: 1px solid #e2e8f0;
    font-size: 0.95rem;
  }

  .growth-memo-content pre {
    background: #0b1220;
    color: #e2e8f0;
    padding: 1rem;
    border-radius: 10px;
    overflow: auto;
    margin: 1rem 0;
  }
`

export function AmazonGrowthMemoViewer() {
  const { memoId } = useParams()
  const [memo, setMemo] = useState<MemoDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!memoId) return
    setLoading(true)
    setError(null)

    memosApi
      .get(memoId)
      .then((data) => {
        setMemo(data)
      })
      .catch((err) => {
        console.error('Failed to fetch growth memo:', err)
        setError('Failed to load growth report')
      })
      .finally(() => {
        setLoading(false)
      })
  }, [memoId])

  const html = memo?.htmlContent || ''
  const markdown = memo?.content || ''

  return (
    <div className="h-full flex flex-col -m-6">
      <style>{growthMemoStyles}</style>

      <div className="p-4 border-b bg-white flex items-center justify-between shadow-sm">
        <div className="flex items-center gap-4">
          <Link to="/reports">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-5 w-5" />
            </Button>
          </Link>
          <div>
            <h1 className="text-lg font-bold text-dashboard-navy">{memo?.title || 'Amazon Growth Report'}</h1>
            <p className="text-sm text-muted-foreground">
              {memo?.customerId || 'Loading...'} - AMAZON GROWTH
            </p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-hidden bg-gray-50">
        <ScrollArea className="h-full">
          <div className="max-w-5xl mx-auto p-6">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="h-8 w-8 animate-spin text-dashboard-navy" />
              </div>
            ) : error ? (
              <div className="flex flex-col items-center justify-center py-20 text-red-600">
                <AlertCircle className="h-12 w-12 mb-4" />
                <p className="text-lg font-medium">{error}</p>
              </div>
            ) : memo ? (
              <div className="bg-white shadow-sm rounded-xl border p-6">
                {html ? (
                  <div
                    className="growth-memo-content"
                    dangerouslySetInnerHTML={{ __html: html }}
                  />
                ) : markdown ? (
                  <div className="growth-memo-content">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                      <FileText className="h-4 w-4" />
                      <span>Rendering raw markdown (HTML not available)</span>
                    </div>
                    <pre>{markdown}</pre>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                    <FileText className="h-12 w-12 mb-4" />
                    <p>No content available</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <FileText className="h-12 w-12 mb-4" />
                <p>No content available</p>
              </div>
            )}
          </div>
        </ScrollArea>
      </div>
    </div>
  )
}
